import { PlusIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  ContactWizard,
  type ContactWizardResult,
} from '@/components/postgrid/contact-wizard';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  ExtractedRecipientInfo,
  usePostgridContactsCreateMutation,
} from '@/services/api/postgridService';

import { ContactCombobox } from './ContactCombobox';

interface ContactComboboxWithCreateProps {
  value?: string | null;
  onChange: (id: string | null, name?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Extracted recipient info from uploaded document - used to pre-populate contact creation */
  extractedRecipient?: ExtractedRecipientInfo | null;
}

/**
 * Enhanced ContactCombobox that includes inline contact creation.
 *
 * When users can't find a contact, they can create one without leaving
 * the current wizard flow. The newly created contact is automatically
 * selected upon successful creation.
 */
export const ContactComboboxWithCreate: React.FC<
  ContactComboboxWithCreateProps
> = ({
  value,
  onChange,
  placeholder = 'Select contact…',
  disabled = false,
  className,
  'aria-label': ariaLabel,
  extractedRecipient,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  // Track the display name for newly created contacts that aren't in the list yet
  const [selectedDisplayName, setSelectedDisplayName] = useState<string | null>(
    null,
  );
  const [createContact, { isLoading: isCreatingContact }] =
    usePostgridContactsCreateMutation();

  // Auto-open the create dialog when extracted recipient info is available
  // and no contact is currently selected
  useEffect(() => {
    if (
      extractedRecipient &&
      !value &&
      (extractedRecipient.confidence ?? 0) >= 0.3
    ) {
      setIsCreating(true);
    }
  }, [extractedRecipient, value]);

  const handleCreateClick = useCallback(() => {
    setIsCreating(true);
  }, []);

  const handleCreateCancel = useCallback(() => {
    setIsCreating(false);
  }, []);

  const handleCreateSuccess = useCallback(
    async (result: ContactWizardResult) => {
      try {
        const newContact = await createContact({
          createPostgridContact: result.payload,
          skipVerification: result.skipVerification,
        }).unwrap();

        toast.success('Contact created successfully');

        // Build display name for the newly created contact
        const displayName =
          [newContact.first_name, newContact.last_name]
            .filter(Boolean)
            .join(' ') ||
          newContact.company_name ||
          'New Contact';

        // Store the display name so it can be shown in the combobox
        setSelectedDisplayName(displayName);

        // Automatically select the newly created contact
        onChange(newContact.id, displayName);

        setIsCreating(false);
      } catch {
        toast.error('Failed to create contact. Please try again.');
      }
    },
    [createContact, onChange],
  );

  return (
    <>
      <div className="space-y-2">
        <ContactCombobox
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          aria-label={ariaLabel}
          selectedDisplayName={selectedDisplayName}
        />

        <button
          type="button"
          onClick={handleCreateClick}
          disabled={disabled}
          className="group flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <PlusIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
          <span>Create new contact</span>
        </button>
      </div>

      {/* Inline Contact Creation Dialog */}
      <Dialog open={isCreating} onClose={handleCreateCancel} size="3xl">
        <DialogTitle>
          {extractedRecipient
            ? 'Create Contact from Extracted Info'
            : 'Create New Contact'}
        </DialogTitle>
        <DialogBody>
          <div className="py-2">
            <ContactWizard
              onSuccess={handleCreateSuccess}
              onCancel={handleCreateCancel}
              isLoading={isCreatingContact}
              extractedRecipient={extractedRecipient}
            />
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};
